<script>
  /**
   * Compras a cuotas: Addi, Sistecrédito, y lo diferido de la tarjeta.
   *
   * ── LA PREGUNTA QUE ESTA PANTALLA RESPONDE ──────────────────────────────────
   *
   * "Compré un bolso en $465.000 a 3 cuotas. ¿Cuándo pago, cuánto pago cada
   * vez, y qué pasa si pago todo de una?"
   *
   * Por eso lo primero que se ve NO es cuánto debes en total: es cuánto te
   * toca soltar ESTE MES entre todas las compras. El total no se paga; las
   * cuotas del mes sí, y ese es el número con el que uno decide.
   *
   * ── CÓMO SE ANOTA LA PLATA ──────────────────────────────────────────────────
   *
   * La compra se registra como GASTO el día que compraste, contra la cuenta
   * de crédito. Ese día ya gastaste: el bolso es tuyo y la deuda existe.
   *
   * Pagar una cuota NO vuelve a ser gasto —sería contarlo dos veces—: es un
   * traslado del banco a la cuenta de crédito, que baja la deuda.
   *
   * Lo único que sí es gasto nuevo al pagar son los INTERESES, y por eso se
   * anotan aparte. Al final del plan, la suma de intereses anotados es
   * exactamente lo que te costó de más haber diferido.
   */
  import {
    db, CUENTAS_CON_CUPO, TIPO_CUENTA, METODO_PAGO, crearCuenta, crearCompraDiferida,
    crearAbono, crearMovimiento, CLASE_ABONO, hoy,
  } from '../lib/db.js';
  import { formatear, interpretar, calcularGmf } from '../lib/dinero.js';
  import { gmfDe, movimientoGmf } from '../lib/gmf.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import {
    planDeCuotas, paramsDe, estadoPlan, liquidarHoy,
    costoDeDiferir, cuotasDelMes, facturaDe,
    facturaPendiente, repartirPagoTarjeta,
  } from '../lib/cuotas.js';
  import { buscarCategoria } from '../lib/categorias.js';

  let {
    abierta = $bindable(false),
    compras = [],
    cuentas = [],
    tarjetas = [],
    entidades = [],
    abonos = [],
    categorias = [],
    enfocar = $bindable(null),   // id de una compra para abrirla directo
    alCambiar = () => {},
  } = $props();

  /** Los intereses van a su categoría: los de tarjeta y los de préstamo son distintos. */
  function categoriaDeIntereses(cuentaCredito) {
    const esTarjeta = cuentaPorId[cuentaCredito]?.tipo === 'tarjeta_credito';
    return buscarCategoria(categorias, esTarjeta ? 'Intereses de tarjeta' : 'Intereses de préstamos')?.id ?? null;
  }

  /**
   * La hora del movimiento. Si se escribió, esa. Si no, y es de hoy, la de
   * ahora. Si es de otro día y no se sabe, sin hora: inventarla sería dato falso.
   */
  function horaFinal(fecha, hora) {
    if (hora) return hora;
    return fecha === hoy() ? new Date().toTimeString().slice(0, 5) : null;
  }

  let vista = $state('lista');   // lista | nueva | detalle | pagar | factura
  let elegida = $state(null);
  let guardando = $state(false);
  let error = $state('');
  let verPagadas = $state(false);

  // ── Formulario de compra nueva ──
  let descripcion = $state('');
  let comercio = $state('');
  let montoTexto = $state('');
  let nCuotas = $state(3);
  let cuentaId = $state(null);
  let fechaCompra = $state(hoy());
  let horaCompra = $state('');
  let sinIntereses = $state(true);
  let tasaTexto = $state('');
  let primeraCuota = $state('');
  // Compras de antes de usar la app: ya están en el saldo de la cuenta de
  // crédito. Anotarlas como gasto otra vez duplicaría la deuda.
  let yaVenia = $state(false);
  let cuotasYaPagadas = $state(0);
  // Quién paga la cuota: uno, o el banco solo (Nequi descuenta el crédito
  // del saldo el día de la cuota, sin que uno haga nada).
  let cobro = $state('manual');
  let cuentaDebito = $state(null);

  // ── Pago de la factura de una tarjeta ──
  let facturaElegida = $state(null);   // { cuenta, tarjeta, compras, factura }
  let montoFacturaTexto = $state('');
  let pagosTarjeta = $state([]);       // los pagos de factura ya hechos, para deshacer

  // ── Pago de una cuota ──
  let cuotaElegida = $state(null);
  let cuentaPago = $state(null);
  let fechaPago = $state(hoy());
  let horaPago = $state('');
  let liquidarTodo = $state(false);

  /**
   * Cómo se pagó, y su 4x1000.
   *
   * Por defecto PSE, porque así se pagan casi siempre Addi y la tarjeta.
   * Desde el banco, el PSE SÍ paga 4x1000 (lo prueba el extracto de Esteban
   * del 15 de sept). La cifra se ve antes de guardar y se puede corregir.
   */
  let metodoPago = $state(METODO_PAGO.PSE);
  let gmfManual = $state(null);
  const METODOS_PAGO = [
    [METODO_PAGO.PSE, 'PSE'],
    [METODO_PAGO.TRANSFERENCIA, 'Transferencia'],
    [METODO_PAGO.DEBITO_AUTOMATICO, 'Débito automático'],
    [METODO_PAGO.OTRO, 'Otro'],
  ];

  const monto = $derived(interpretar(montoTexto) ?? 0);
  const tasa = $derived(sinIntereses ? null : (parseFloat(tasaTexto.replace(',', '.')) || null));

  const cuentasCredito = $derived(
    cuentas.filter((c) => !c.archivada && CUENTAS_CON_CUPO.includes(c.tipo))
  );
  const cuentasLiquidas = $derived(
    cuentas.filter((c) => !c.archivada &&
      ['efectivo', 'ahorros', 'corriente', 'billetera'].includes(c.tipo))
  );
  const cuentaPorId = $derived(Object.fromEntries(cuentas.map((c) => [c.id, c])));

  $effect(() => {
    if (cuentasCredito.length && !cuentaId) cuentaId = cuentasCredito[0].id;
    if (cuentasLiquidas.length && !cuentaPago) cuentaPago = cuentasLiquidas[0].id;
    if (cuentasLiquidas.length && !cuentaDebito) {
      cuentaDebito = (cuentasLiquidas.find((c) => c.tipo === 'billetera') ?? cuentasLiquidas[0]).id;
    }
  });

  /** La tarjeta (si la hay) de la cuenta elegida: de ahí sale la fecha de corte. */
  const tarjetaDe = (cid) => tarjetas.find((t) => t.cuenta_id === cid && !t.borrado_en) ?? null;

  const abonosDe = (compraId) =>
    abonos.filter((a) => a.compra_id === compraId && !a.borrado_en);

  const planDe = (c) =>
    c.plan?.length ? c.plan : planDeCuotas(paramsDe(c, tarjetaDe(c.cuenta_id)));

  // ── El plan que se está armando, en vivo mientras uno escribe ──
  const planPrevio = $derived(
    monto > 0 && nCuotas > 0
      ? planDeCuotas({
          monto,
          cuotas: Number(nCuotas),
          fechaCompra,
          tasaEa: tasa,
          tarjeta: tarjetaDe(cuentaId),
          primeraCuota: primeraCuota || null,
        })
      : []
  );

  const vivas = $derived(compras.filter((c) => !c.borrado_en && !c.pagada));
  const pagadas = $derived(compras.filter((c) => !c.borrado_en && c.pagada));

  /** Lo de este mes: el número con el que uno de verdad decide. */
  const esteMes = $derived(
    cuotasDelMes(
      vivas.map((c) => ({ ...c, plan: planDe(c), abonos: abonosDe(c.id) })),
      hoy().slice(0, 7)
    )
  );

  /**
   * Las cuotas del mes, con las de tarjeta juntas en su factura.
   * Once cuotas de Temu en Nu no son once pagos: son UNA factura.
   */
  const gruposDelMes = $derived.by(() => {
    const grupos = new Map();
    for (const f of esteMes.filas) {
      const esTarjeta = Boolean(tarjetaDe(f.cuenta_id));
      const clave = esTarjeta ? `t-${f.cuenta_id}-${f.fecha}` : `c-${f.compra_id}-${f.numero}`;
      const g = grupos.get(clave) ?? {
        clave, fecha: f.fecha, valor: 0, n: 0, compras: new Set(),
        titulo: esTarjeta ? `Factura ${cuentaPorId[f.cuenta_id]?.nombre ?? 'de la tarjeta'}` : f.descripcion,
        meta: esTarjeta ? '' : `Cuota ${f.numero} de ${f.de}${f.comercio ? ' · ' + f.comercio : ''}`,
        esTarjeta,
      };
      g.valor += f.valor;
      g.n += 1;
      g.compras.add(f.compra_id);
      grupos.set(clave, g);
    }
    return [...grupos.values()].map((g) => g.esTarjeta
      ? { ...g, meta: `${g.n} cuota${g.n === 1 ? '' : 's'} de ${g.compras.size} compra${g.compras.size === 1 ? '' : 's'}` }
      : g);
  });

  /** Cada tarjeta con compras vivas, y su próxima factura. */
  const facturas = $derived.by(() => {
    const porCuenta = new Map();
    for (const c of vivas) {
      if (!tarjetaDe(c.cuenta_id)) continue;
      if (!porCuenta.has(c.cuenta_id)) porCuenta.set(c.cuenta_id, []);
      porCuenta.get(c.cuenta_id).push({ ...c, plan: planDe(c), abonos: abonosDe(c.id) });
    }
    return [...porCuenta.entries()]
      .map(([cid, lista]) => ({
        cuenta: cuentaPorId[cid], tarjeta: tarjetaDe(cid), compras: lista,
        factura: facturaPendiente(lista, hoy()),
      }))
      .filter((x) => x.cuenta && x.factura);
  });

  /**
   * Si lo que pagó no es lo que calculó la app, ¿por qué?
   *
   * Los intereses de la app son un cálculo; los de Nu salen del extracto.
   * Si Nu dice $119.600 y la app $119.593, esos $7 son INTERESES, no un
   * abono extra. Contarlos como abono dejaría la deuda de la tarjeta por
   * debajo de la real. Pero si paga $150.000 redondos, eso sí es abonar de
   * más. Por defecto: diferencias chicas son intereses; grandes, abono.
   */
  let diferenciaEs = $state(null);   // null (la app decide) | 'interes' | 'abono'
  const diferenciaFactura = $derived(
    vista === 'factura' && facturaElegida ? valorDelPago - facturaElegida.factura.total : 0
  );
  const puedeSerInteres = $derived(
    facturaElegida != null && diferenciaFactura !== 0 &&
    -diferenciaFactura < facturaElegida.factura.interes
  );
  const modoDiferencia = $derived(
    diferenciaFactura === 0 ? null
    : !puedeSerInteres ? 'abono'
    : diferenciaEs ?? (Math.abs(diferenciaFactura) <= Math.max(2000, facturaElegida.factura.interes * 0.5)
        ? 'interes' : 'abono')
  );

  /** Cómo se reparte el pago de la factura, en vivo mientras se escribe. */
  const repartoFactura = $derived(
    vista === 'factura' && facturaElegida && valorDelPago > 0
      ? repartirPagoTarjeta(
          facturaElegida.compras,
          modoDiferencia === 'interes' ? facturaElegida.factura.total : valorDelPago,
          facturaElegida.factura.limite)
      : null
  );

  const deudaTotal = $derived(
    vivas.reduce((t, c) => {
      const e = estadoPlan(planDe(c), abonosDe(c.id));
      return t + e.pendiente;
    }, 0)
  );

  const estadoElegida = $derived(
    elegida ? estadoPlan(planDe(elegida), abonosDe(elegida.id)) : null
  );
  const liquidacion = $derived(
    elegida ? liquidarHoy(planDe(elegida), abonosDe(elegida.id)) : null
  );

  // ── El 4x1000 del pago que se está anotando ──
  const cuentaDelPago = $derived(cuentaPorId[cuentaPago]);
  const pagoEsEfectivo = $derived(cuentaDelPago?.tipo === 'efectivo');
  const metodoReal = $derived(pagoEsEfectivo ? METODO_PAGO.EFECTIVO : metodoPago);
  const valorDelPago = $derived(
    vista === 'factura' ? (interpretar(montoFacturaTexto) ?? 0)
    : liquidarTodo ? (liquidacion?.capital ?? 0) : (cuotaElegida?.pendiente ?? 0)
  );
  const gmfAutoPago = $derived(gmfDe({
    tipo: 'transferencia', monto: valorDelPago, cuentaOrigen: cuentaDelPago,
    entidad: entidades.find((e) => e.id === cuentaDelPago?.entidad_id) ?? null,
    metodoPago: metodoReal,
  }));
  const gmfPago = $derived(gmfManual ?? gmfAutoPago.monto);

  /** Abrir una compra directo, cuando se llega desde un movimiento. */
  $effect(() => {
    if (!enfocar) return;
    const c = compras.find((x) => x.id === enfocar);
    if (c) { elegida = c; vista = 'detalle'; }
    enfocar = null;
  });

  /** Si la compra cambió (un pago nuevo), releerla de la lista viva. */
  $effect(() => {
    if (!elegida) return;
    const fresca = compras.find((c) => c.id === elegida.id);
    if (fresca && fresca !== elegida) elegida = fresca;
  });

  function nueva() {
    descripcion = '';
    comercio = '';
    montoTexto = '';
    nCuotas = 3;
    fechaCompra = hoy();
    horaCompra = '';
    sinIntereses = true;
    tasaTexto = '';
    primeraCuota = '';
    yaVenia = false;
    cuotasYaPagadas = 0;
    cobro = 'manual';
    vista = 'nueva';
  }

  /** Si no hay ninguna cuenta de crédito, se crea Addi sin preguntar mucho. */
  async function crearCuentaCredito(nombre, tipo) {
    const nueva = crearCuenta({
      nombre,
      tipo,
      icono: tipo === TIPO_CUENTA.CREDITO_ROTATIVO ? '🛍️' : '💳',
      orden: 50,
    });
    await guardar('cuentas', nueva);
    cuentaId = nueva.id;
    alCambiar();
  }

  async function registrar() {
    if (guardando || monto <= 0 || !cuentaId || !planPrevio.length) return;
    guardando = true;
    try {
      const plan = planPrevio;
      const yaPagadas = yaVenia
        ? Math.max(0, Math.min(Number(cuotasYaPagadas) || 0, plan.length - 1))
        : 0;
      const compra = crearCompraDiferida({
        cuenta_id: cuentaId,
        tarjeta_id: tarjetaDe(cuentaId)?.id ?? null,
        descripcion: descripcion.trim() || comercio.trim() || 'Compra',
        comercio: comercio.trim(),
        monto_total: monto,
        cuotas_totales: Number(nCuotas),
        cuotas_pagadas: yaPagadas,
        cuotas_pendientes: Number(nCuotas) - yaPagadas,
        ya_incluida: yaVenia,
        valor_cuota: plan[0].valor,
        saldo_pendiente: plan.reduce((t, c) => t + c.valor, 0),
        tasa_ea: tasa,
        sin_intereses: sinIntereses,
        total_intereses: costoDeDiferir(plan),
        fecha_compra: fechaCompra,
        fecha_primera_cuota: plan[0].fecha,
        fecha_proxima_cuota: plan[yaPagadas]?.fecha ?? plan[0].fecha,
        fecha_ultima_cuota: plan[plan.length - 1].fecha,
        cobro,
        cuenta_debito_id: cobro === 'debito_automatico' ? cuentaDebito : null,
      });
      await guardar('compras_diferidas', compra);

      // Las cuotas que se pagaron antes de usar la app: quedan como pagadas,
      // sin movimiento, porque esa plata ya salió antes de que existiera aquí.
      if (yaPagadas > 0) {
        await guardar('abonos', crearAbono({
          compra_id: compra.id,
          clase: 'ajuste',
          fecha: plan[yaPagadas - 1].fecha,
          monto: plan.slice(0, yaPagadas).reduce((t, c) => t + c.valor, 0),
          cubre_cuotas: plan.slice(0, yaPagadas).map((c) => c.numero),
          nota: 'Cuotas pagadas antes de usar la app.',
        }));
      }

      // La compra en sí: gasto contra la cuenta de crédito, el día que fue.
      // La deuda nace aquí, no cuando llegue la primera cuota. Si ya venía
      // de antes, NO: ya está en el saldo de la cuenta y sería contarla dos veces.
      if (!yaVenia) await guardar('movimientos', crearMovimiento({
        tipo: 'egreso',
        monto,
        fecha: fechaCompra, hora: horaFinal(fechaCompra, horaCompra),
        concepto: compra.descripcion,
        contraparte: comercio.trim(),
        cuenta_origen: cuentaId,
        metodo_pago: 'credito',
        compra_diferida_id: compra.id,
        notas: `Diferida a ${nCuotas} cuota${nCuotas === 1 ? '' : 's'}.`,
      }));

      vista = 'lista';
      alCambiar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  function abrirDetalle(c) {
    elegida = c;
    vista = 'detalle';
  }

  function abrirPago(cuota, todo = false) {
    cuotaElegida = cuota;
    liquidarTodo = todo;
    fechaPago = hoy();
    horaPago = '';
    metodoPago = METODO_PAGO.PSE;
    gmfManual = null;
    vista = 'pagar';
  }

  async function registrarPago() {
    if (guardando || !elegida) return;
    guardando = true;
    try {
      const plan = planDe(elegida);
      const prev = estadoPlan(plan, abonosDe(elegida.id));
      const credito = elegida.cuenta_id;

      // Cuánto sale del bolsillo y cuánto de eso es interés.
      const valor = liquidarTodo ? liquidacion.capital : cuotaElegida.pendiente;
      const interes = liquidarTodo ? 0 : cuotaElegida.interes;

      // 1. El traslado: baja la deuda. NO es gasto, ya se contó al comprar.
      const traslado = crearMovimiento({
        tipo: 'transferencia',
        monto: valor,
        fecha: fechaPago, hora: horaFinal(fechaPago, horaPago),
        concepto: liquidarTodo
          ? `Liquidaste ${elegida.descripcion}`
          : `Cuota ${cuotaElegida.numero} de ${plan.length} · ${elegida.descripcion}`,
        contraparte: elegida.comercio,
        metodo_pago: metodoReal,
        cuenta_origen: cuentaPago,
        cuenta_destino: credito,
        compra_diferida_id: elegida.id,
        gmf: gmfPago,
        gmf_revisado: true,
      });
      await guardar('movimientos', traslado);

      // El 4x1000 del pago, si salió de una cuenta que lo cobra.
      if (gmfPago > 0) {
        await guardar('movimientos', crearMovimiento(movimientoGmf(traslado, gmfPago)));
      }

      // 2. Los intereses: esto SÍ es gasto nuevo. Es lo que costó diferir.
      if (interes > 0) {
        await guardar('movimientos', crearMovimiento({
          tipo: 'egreso',
          monto: interes,
          fecha: fechaPago, hora: horaFinal(fechaPago, horaPago),
          concepto: `Intereses · ${elegida.descripcion}`,
          contraparte: elegida.comercio,
          cuenta_origen: credito,
          compra_diferida_id: elegida.id,
          categoria_id: categoriaDeIntereses(credito),
          notas: 'Lo que costó de más pagar a cuotas.',
        }));
      }

      const pendientes = prev.filas.filter((f) => !f.pagada);
      await guardar('abonos', crearAbono({
        compra_id: elegida.id,
        clase: CLASE_ABONO.ABONO,
        fecha: fechaPago, hora: horaFinal(fechaPago, horaPago),
        monto: liquidarTodo ? liquidacion.siPagaCuotas : valor,
        cuenta_id: cuentaPago,
        cubre_cuotas: liquidarTodo
          ? pendientes.map((f) => f.numero)
          : [cuotaElegida.numero],
        saldo_antes: prev.pendiente,
        saldo_despues: liquidarTodo ? 0 : prev.pendiente - valor,
        nota: liquidarTodo && liquidacion.ahorro > 0
          ? `Prepago: pagó ${formatear(liquidacion.capital)} en vez de ` +
            `${formatear(liquidacion.siPagaCuotas)}. Se ahorró ${formatear(liquidacion.ahorro)}.`
          : '',
      }));

      const nuevoEstado = estadoPlan(plan, [
        ...abonosDe(elegida.id),
        { monto: liquidarTodo ? liquidacion.siPagaCuotas : valor },
      ]);

      await guardar('compras_diferidas', {
        ...elegida,
        cuotas_pagadas: liquidarTodo ? plan.length : nuevoEstado.cuotasPagadas,
        cuotas_pendientes: liquidarTodo ? 0 : plan.length - nuevoEstado.cuotasPagadas,
        saldo_pendiente: liquidarTodo ? 0 : nuevoEstado.pendiente,
        fecha_proxima_cuota: nuevoEstado.proxima?.fecha ?? null,
        pagada: liquidarTodo || nuevoEstado.saldada,
      });

      vista = 'detalle';
      cuotaElegida = null;
      liquidarTodo = false;
      alCambiar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  async function borrarCompra() {
    if (!elegida) return;
    if (!confirm(`¿Borrar "${elegida.descripcion}"? Se quitan también sus movimientos.`)) return;
    guardando = true;
    try {
      const movs = await db.movimientos.where('compra_diferida_id').equals(elegida.id).toArray();
      for (const m of movs) await borrar('movimientos', m.id);
      for (const a of abonosDe(elegida.id)) await borrar('abonos', a.id);
      await borrar('compras_diferidas', elegida.id);
      vista = 'lista';
      elegida = null;
      alCambiar();
    } catch (e) {
      // Nunca en silencio: si algo falla, la pantalla lo dice.
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  /**
   * Quién paga la cuota de una compra que ya existe. El crédito de Nequi,
   * por ejemplo, lo descuenta Nequi solo: se cambia aquí una vez y de ahí
   * en adelante la app anota cada descuento sin que uno haga nada.
   */
  async function cambiarCobro(nuevo, cuenta = null) {
    if (!elegida) return;
    try {
      await guardar('compras_diferidas', {
        ...elegida,
        cobro: nuevo,
        cuenta_debito_id: nuevo === 'debito_automatico' ? (cuenta ?? elegida.cuenta_debito_id ?? cuentaDebito) : null,
      });
      alCambiar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    }
  }

  // ── La factura de la tarjeta ──────────────────────────────────────────────

  async function abrirFactura(f) {
    facturaElegida = f;
    montoFacturaTexto = String(f.factura.total);
    diferenciaEs = null;
    fechaPago = hoy();
    horaPago = '';
    metodoPago = METODO_PAGO.PSE;
    gmfManual = null;
    error = '';
    vista = 'factura';
    await cargarPagosTarjeta(f.cuenta.id);
  }

  async function cargarPagosTarjeta(cuentaTarjeta) {
    const lista = await db.movimientos.where('cuenta_destino').equals(cuentaTarjeta).toArray();
    pagosTarjeta = lista
      .filter((m) => m.pago_tarjeta && !m.borrado_en)
      .sort((a, b) => (b.fecha + (b.hora ?? '')).localeCompare(a.fecha + (a.hora ?? '')));
  }

  /** Deja una compra con sus contadores al día, según sus abonos vivos. */
  async function actualizarCompra(c, abonosVivos) {
    const plan = planDe(c);
    const e = estadoPlan(plan, abonosVivos);
    await guardar('compras_diferidas', {
      ...c,
      cuotas_pagadas: e.cuotasPagadas,
      cuotas_pendientes: plan.length - e.cuotasPagadas,
      saldo_pendiente: e.pendiente,
      fecha_proxima_cuota: e.proxima?.fecha ?? null,
      pagada: e.saldada,
    });
  }

  /**
   * UN pago a la tarjeta, repartido entre todas sus compras.
   *
   * En el banco sale una sola línea ("PSE Nu $119.350"), y así queda aquí:
   * un solo traslado, con su 4x1000 si sale del banco. Por dentro, cada
   * compra recibe su abono, y los intereses del mes van juntos como un gasto,
   * que es como los muestra el extracto de la tarjeta.
   */
  async function registrarPagoFactura() {
    if (guardando || !facturaElegida || !repartoFactura?.partes.length) return;
    guardando = true;
    error = '';
    try {
      const { cuenta, factura } = facturaElegida;
      const { partes } = repartoFactura;
      // Lo que Nu cobró de intereses por encima (o por debajo) del cálculo.
      const ajusteInteres = modoDiferencia === 'interes' ? diferenciaFactura : 0;
      const aplicado = partes.reduce((t, p) => t + p.monto, 0) + ajusteInteres;
      const hora = horaFinal(fechaPago, horaPago);

      const traslado = crearMovimiento({
        tipo: 'transferencia',
        monto: aplicado,
        fecha: fechaPago, hora,
        concepto: `Pago tarjeta ${cuenta.nombre} · factura del ${fechaCorta(factura.limite)}`,
        contraparte: cuenta.nombre,
        metodo_pago: metodoReal,
        cuenta_origen: cuentaPago,
        cuenta_destino: cuenta.id,
        pago_tarjeta: true,
        gmf: gmfPago,
        gmf_revisado: true,
        notas: partes.map((p) => `${p.compra.descripcion} ${formatear(p.monto)}`).join(' · '),
      });
      await guardar('movimientos', traslado);
      if (gmfPago > 0) {
        await guardar('movimientos', {
          ...crearMovimiento(movimientoGmf(traslado, gmfPago)), pago_tarjeta: true,
        });
      }

      const interesCalculado = partes.reduce((t, p) => t + p.interes, 0);
      const interes = interesCalculado + ajusteInteres;
      if (interes > 0) {
        await guardar('movimientos', crearMovimiento({
          tipo: 'egreso',
          monto: interes,
          fecha: fechaPago, hora,
          concepto: `Intereses tarjeta ${cuenta.nombre}`,
          contraparte: cuenta.nombre,
          cuenta_origen: cuenta.id,
          pago_tarjeta: true,
          pago_de: traslado.id,
          categoria_id: categoriaDeIntereses(cuenta.id),
          notas: ajusteInteres
            ? `Los de ${cuenta.nombre}: la app calculaba ${formatear(interesCalculado)}.`
            : 'Lo que costó pagar a cuotas este mes.',
        }));
      }

      for (const p of partes) {
        const original = compras.find((x) => x.id === p.compra.id);
        if (!original) continue;
        const previos = abonosDe(original.id);
        const antes = estadoPlan(planDe(original), previos);
        const abono = crearAbono({
          compra_id: original.id,
          movimiento_id: traslado.id,
          clase: CLASE_ABONO.ABONO,
          fecha: fechaPago, hora,
          monto: p.monto,
          cuenta_id: cuentaPago,
          metodo_pago: metodoReal,
          cubre_cuotas: [...new Set(p.cubre)],
          saldo_antes: antes.pendiente,
          saldo_despues: Math.max(0, antes.pendiente - p.monto),
          nota: `Pago de la factura del ${fechaCorta(factura.limite)}.`,
        });
        await guardar('abonos', abono);
        await actualizarCompra(original, [...previos, abono]);
      }

      vista = 'lista';
      facturaElegida = null;
      alCambiar();
    } catch (e) {
      error = `No se pudo guardar: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  /** Deshace un pago de factura: el traslado, su 4x1000, sus intereses y sus abonos. */
  async function deshacerPagoTarjeta(pago) {
    if (!confirm(`¿Deshacer el pago de ${formatear(pago.monto)} del ${fechaCorta(pago.fecha)}?`)) return;
    guardando = true;
    error = '';
    try {
      const hijos = await db.movimientos.where('movimiento_padre_id').equals(pago.id).toArray();
      const intereses = (await db.movimientos.where('cuenta_origen').equals(pago.cuenta_destino).toArray())
        .filter((m) => m.pago_de === pago.id);
      for (const m of [...hijos, ...intereses]) if (!m.borrado_en) await borrar('movimientos', m.id);

      const suyos = abonos.filter((a) => a.movimiento_id === pago.id && !a.borrado_en);
      for (const a of suyos) await borrar('abonos', a.id);
      await borrar('movimientos', pago.id);

      for (const cid of new Set(suyos.map((a) => a.compra_id))) {
        const c = compras.find((x) => x.id === cid);
        if (c) await actualizarCompra(c, abonosDe(cid).filter((a) => a.movimiento_id !== pago.id));
      }
      // La factura que estaba abierta ya no es la que toca: se vuelve a la
      // lista, que la recalcula con lo que quedó.
      vista = 'lista';
      facturaElegida = null;
      alCambiar();
    } catch (e) {
      error = `No se pudo deshacer: ${e?.message ?? e}`;
      console.error(e);
    } finally {
      guardando = false;
    }
  }

  function fechaCorta(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00')
      .toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  }
  function fechaLarga(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00')
      .toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  const titulo = $derived(
    vista === 'nueva' ? 'Compra a cuotas'
    : vista === 'pagar' ? (liquidarTodo ? 'Pagar todo' : 'Pagar cuota')
    : vista === 'factura' ? `Factura ${facturaElegida?.cuenta?.nombre ?? ''}`
    : vista === 'detalle' ? elegida?.descripcion ?? ''
    : 'Cuotas'
  );

  function atras() {
    error = '';
    if (vista === 'pagar') vista = 'detalle';
    else if (vista === 'factura') { vista = 'lista'; facturaElegida = null; }
    else if (vista === 'detalle' || vista === 'nueva') { vista = 'lista'; elegida = null; }
    else abierta = false;
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={atras} aria-label="Volver">←</button>
      <h1>{titulo}</h1>
    </header>

    <div class="contenido">
      {#if error}<p class="error" role="alert">{error}</p>{/if}

      <!-- ══ LISTA ══════════════════════════════════════════════════════ -->
      {#if vista === 'lista'}
        <div class="destacado">
          <p class="rotulo">Este mes te toca pagar</p>
          <p class="cifra grande">{formatear(esteMes.total)}</p>
          {#if deudaTotal > esteMes.total}
            <p class="ayuda">
              De {formatear(deudaTotal)} que debes en total. El total no se
              paga de una: se pagan las cuotas del mes.
            </p>
          {:else if !esteMes.total}
            <p class="ayuda">Este mes no tienes cuotas pendientes.</p>
          {/if}
        </div>

        <button class="principal ancho" onclick={nueva}>Anotar una compra a cuotas</button>

        {#if facturas.length}
          <section>
            <h2>Facturas de tarjeta</h2>
            {#each facturas as f (f.cuenta.id)}
              {@const vencida = f.factura.limite < hoy()}
              <div class="factura" class:vencida>
                <div class="factura-cabeza">
                  <p class="persona">💳 {f.cuenta.nombre}</p>
                  <span class="cifra monto">{formatear(f.factura.total)}</span>
                </div>
                <p class="meta" class:tarde={vencida}>
                  {vencida ? 'Se pasó el ' : 'Pagas hasta el '}{fechaLarga(f.factura.limite)}
                  · {f.factura.filas.length} compra{f.factura.filas.length === 1 ? '' : 's'}
                </p>
                {#if f.factura.interes > 0}
                  <p class="meta">
                    {formatear(f.factura.capital)} de las compras
                    + {formatear(f.factura.interes)} de intereses (aprox.)
                  </p>
                {/if}
                <p class="ayuda">
                  Corte el {f.tarjeta.dia_corte}, pago el {f.tarjeta.dia_limite_pago}. Lo que compres
                  hasta el corte entra en esta factura; lo de después, en la siguiente.
                </p>
                <button class="secundario" onclick={() => abrirFactura(f)}>Pagar la factura</button>
              </div>
            {/each}
          </section>
        {/if}

        {#if gruposDelMes.length}
          <section>
            <h2>Las cuotas de este mes</h2>
            {#each gruposDelMes as g (g.clave)}
              {@const vencida = g.fecha < hoy()}
              <div class="cuota-mes" class:vencida>
                <span class="dia cifra">{fechaCorta(g.fecha)}</span>
                <div class="datos">
                  <p class="persona">{g.titulo}</p>
                  <p class="meta">
                    {g.meta}
                    {#if vencida} · <span class="tarde">se pasó</span>{/if}
                  </p>
                </div>
                <span class="cifra monto">{formatear(g.valor)}</span>
              </div>
            {/each}
          </section>
        {/if}

        {#if vivas.length}
          <section>
            <h2>Compras en curso</h2>
            {#each vivas as c (c.id)}
              {@const e = estadoPlan(planDe(c), abonosDe(c.id))}
              <button class="fila" onclick={() => abrirDetalle(c)}>
                <div class="datos">
                  <p class="persona">{c.descripcion}</p>
                  <p class="meta" class:tarde={e.enMora}>
                    {e.cuotasPagadas} de {e.cuotasTotales} pagadas
                    {#if e.enMora}
                      · atrasada {e.diasMora} día{e.diasMora === 1 ? '' : 's'}
                    {:else if e.proxima}
                      · la próxima el {fechaCorta(e.proxima.fecha)}
                    {/if}
                  </p>
                  <div class="barra" aria-hidden="true">
                    <div class="lleno" style="width: {Math.round((e.pagado / e.total) * 100)}%"></div>
                  </div>
                </div>
                <div class="derecha">
                  <span class="cifra monto">{formatear(e.pendiente)}</span>
                  <span class="meta">falta</span>
                </div>
              </button>
            {/each}
          </section>
        {:else}
          <div class="vacio">
            <p class="vacio-titulo">No hay compras a cuotas</p>
            <p class="explica">
              Anota lo que compraste con Addi, con Sistecrédito o diferido en
              la tarjeta. La app arma el calendario completo: qué día cae cada
              cuota, cuánto vale, y cuánto te ahorras si pagas todo de una.
            </p>
          </div>
        {/if}

        {#if pagadas.length}
          <section>
            <button class="plegable" onclick={() => (verPagadas = !verPagadas)}>
              <span>Ya pagadas · {pagadas.length}</span>
              <span class="flecha" class:abierta={verPagadas} aria-hidden="true">›</span>
            </button>
            {#if verPagadas}
              {#each pagadas as c (c.id)}
                <button class="fila cerrada" onclick={() => abrirDetalle(c)}>
                  <div class="datos">
                    <p class="persona">{c.descripcion}</p>
                    <p class="meta">
                      {c.cuotas_totales} cuota{c.cuotas_totales === 1 ? '' : 's'}
                      {#if c.total_intereses > 0}
                        · costó {formatear(c.total_intereses)} de más
                      {/if}
                    </p>
                  </div>
                  <span class="cifra monto apagado">{formatear(c.monto_total)}</span>
                </button>
              {/each}
            {/if}
          </section>
        {/if}

      <!-- ══ NUEVA COMPRA ═══════════════════════════════════════════════ -->
      {:else if vista === 'nueva'}
        {#if !cuentasCredito.length}
          <div class="aviso">
            <p><strong>Primero hace falta la cuenta de donde sale el crédito.</strong></p>
            <p class="explica">
              Addi y Sistecrédito no son tarjetas: cada compra es su propio
              crédito con sus propias fechas. Por eso van aparte.
            </p>
            <div class="botones">
              <button class="secundario" onclick={() => crearCuentaCredito('Addi', TIPO_CUENTA.CREDITO_ROTATIVO)}>
                Crear Addi
              </button>
              <button class="secundario" onclick={() => crearCuentaCredito('Tarjeta', TIPO_CUENTA.TARJETA_CREDITO)}>
                Crear tarjeta
              </button>
            </div>
          </div>
        {:else}
          <label>
            <span class="etiqueta">¿Qué compraste?</span>
            <input type="text" bind:value={descripcion} placeholder="Un bolso" />
          </label>

          <div class="par">
            <label>
              <span class="etiqueta">Dónde</span>
              <input type="text" bind:value={comercio} placeholder="Comercio" />
            </label>
            <label>
              <span class="etiqueta">Cuándo</span>
              <div class="fecha-hora">
              <input type="date" bind:value={fechaCompra} max={hoy()} />
              <input type="time" bind:value={horaCompra} aria-label="Hora (opcional)" />
            </div>
            </label>
          </div>

          <label>
            <span class="etiqueta">Cuánto costó</span>
            <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={montoTexto} placeholder="0" />
          </label>

          <label>
            <span class="etiqueta">¿A cuántas cuotas?</span>
            <div class="chips">
              {#each [1, 3, 6, 12, 24, 36] as n}
                <button class="chip" class:activo={Number(nCuotas) === n} onclick={() => (nCuotas = n)}>
                  {n}
                </button>
              {/each}
              <input class="chip-num cifra" type="number" min="1" max="72" bind:value={nCuotas} />
            </div>
          </label>

          <label>
            <span class="etiqueta">Con qué</span>
            <select bind:value={cuentaId}>
              {#each cuentasCredito as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
            </select>
          </label>

          <label>
            <span class="etiqueta">Fecha de la primera cuota</span>
            <input type="date" bind:value={primeraCuota} />
            <span class="ayuda">
              La que te dio {cuentaPorId[cuentaId]?.nombre ?? 'la entidad'}. Si la dejas vacía,
              se cuenta un mes después de la compra (o con la fecha de corte, si es tarjeta).
            </span>
          </label>

          <label class="casilla">
            <input type="checkbox" bind:checked={yaVenia} />
            <span>
              La compré antes de usar la app: ya está incluida en lo que debo en
              {cuentaPorId[cuentaId]?.nombre ?? 'esa cuenta'}. No contarla como gasto otra vez.
            </span>
          </label>
          {#if yaVenia}
            <label>
              <span class="etiqueta">¿Cuántas cuotas pagaste antes de hoy?</span>
              <input class="cifra corto" type="number" min="0" max={Math.max(0, Number(nCuotas) - 1)} bind:value={cuotasYaPagadas} />
              <span class="ayuda">
                Esas quedan marcadas como pagadas. La que pagues de aquí en
                adelante la anotas con "Pagar" en su cuota.
              </span>
            </label>
          {/if}

          <div class="bloque">
            <span class="etiqueta">¿Quién paga cada cuota?</span>
            <div class="opciones">
              <button class="opcion" class:activa={cobro === 'manual'} onclick={() => (cobro = 'manual')}>
                <span class="opcion-titulo">Yo la pago</span>
                <span class="opcion-nota">Por PSE, transferencia o en efectivo. La marcas con "Pagar".</span>
              </button>
              <button class="opcion" class:activa={cobro === 'debito_automatico'} onclick={() => (cobro = 'debito_automatico')}>
                <span class="opcion-titulo">Me la descuentan solos</span>
                <span class="opcion-nota">Como el crédito de Nequi: se lleva lo que haya el día de la cuota.</span>
              </button>
            </div>
            {#if cobro === 'debito_automatico'}
              <label>
                <span class="etiqueta">¿De qué cuenta la descuentan?</span>
                <select bind:value={cuentaDebito}>
                  {#each cuentasLiquidas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
                </select>
                <span class="ayuda">
                  La app lo anota sola el día de la cuota. Si esa cuenta no tiene
                  plata completa, anota lo que había y el resto cuando entre.
                </span>
              </label>
            {/if}
          </div>

          {#if tarjetaDe(cuentaId)}
            {@const f = facturaDe(fechaCompra, tarjetaDe(cuentaId))}
            <p class="ayuda">
              Con corte el {tarjetaDe(cuentaId).dia_corte} y pago el
              {tarjetaDe(cuentaId).dia_limite_pago}, esta compra entra en la
              factura que se paga el {fechaLarga(f.limite)}:
              <strong>{f.diasDeFinanciacion} días</strong> de plazo.
            </p>
          {/if}

          <div class="opciones">
            <button class="opcion" class:activa={sinIntereses} onclick={() => (sinIntereses = true)}>
              <span class="opcion-titulo">Sin intereses</span>
              <span class="opcion-nota">Las promociones de 3 cuotas de Addi, o diferido a 0%.</span>
            </button>
            <button class="opcion" class:activa={!sinIntereses} onclick={() => (sinIntereses = false)}>
              <span class="opcion-titulo">Con intereses</span>
              <span class="opcion-nota">Escribe la tasa efectiva anual que te dijeron.</span>
            </button>
          </div>

          {#if !sinIntereses}
            <label>
              <span class="etiqueta">Tasa efectiva anual (%)</span>
              <input class="cifra" type="text" inputmode="decimal" bind:value={tasaTexto} placeholder="28,69" />
              <span class="ayuda">
                Va en el contrato o en la app de la entidad. Si no la sabes, la
                usura de hoy es el techo legal: por encima de ahí es delito.
              </span>
            </label>
          {/if}

          <!-- El calendario, EN VIVO. Es lo que convierte "3 cuotas" en algo
               que uno puede mirar y decidir. -->
          {#if planPrevio.length}
            <section class="previo">
              <h2>Así quedaría</h2>
              <ol class="plan">
                {#each planPrevio as c (c.numero)}
                  <li>
                    <span class="num">{c.numero}</span>
                    <div class="datos">
                      <p class="persona cifra">{formatear(c.valor)}</p>
                      <p class="meta">
                        {fechaLarga(c.fecha)}
                        {#if c.interes > 0} · {formatear(c.interes)} de interés{/if}
                      </p>
                    </div>
                  </li>
                {/each}
              </ol>
              {#if costoDeDiferir(planPrevio) > 0}
                <p class="explica ojo">
                  Diferir te cuesta <strong>{formatear(costoDeDiferir(planPrevio))}</strong> de
                  más. Terminas pagando {formatear(monto + costoDeDiferir(planPrevio))}
                  por algo de {formatear(monto)}.
                </p>
              {:else if Number(nCuotas) > 1}
                <p class="explica">
                  Sin intereses: pagas exactamente {formatear(monto)}, repartidos.
                </p>
              {/if}
            </section>
          {/if}

          <div class="botones">
            <button class="secundario" onclick={() => (vista = 'lista')}>Cancelar</button>
            <button class="principal" disabled={monto <= 0 || guardando} onclick={registrar}>
              {guardando ? 'Guardando…' : 'Anotar la compra'}
            </button>
          </div>
        {/if}

      <!-- ══ DETALLE DE UNA COMPRA ══════════════════════════════════════ -->
      {:else if vista === 'detalle' && elegida && estadoElegida}
        {@const plan = planDe(elegida)}
        {@const e = estadoElegida}
        {@const filas = elegida.pagada
          ? plan.map((c) => ({ ...c, pagada: true, pendiente: 0, abonado: c.valor }))
          : e.filas}

        <div class="destacado" class:saldada={elegida.pagada}>
          <p class="rotulo">{elegida.pagada ? 'Ya la pagaste' : 'Te falta'}</p>
          <p class="cifra grande">{formatear(elegida.pagada ? elegida.monto_total : e.pendiente)}</p>
          <p class="frase" class:tarde={e.enMora && !elegida.pagada}>
            {#if elegida.pagada}
              {elegida.cuotas_totales} cuota{elegida.cuotas_totales === 1 ? '' : 's'}
              {#if elegida.comercio} · {elegida.comercio}{/if}
            {:else if e.enMora}
              Te atrasaste {e.diasMora} día{e.diasMora === 1 ? '' : 's'}
            {:else if e.proxima}
              Van {e.cuotasPagadas} de {e.cuotasTotales} · la próxima el {fechaCorta(e.proxima.fecha)}
            {/if}
          </p>
          <div class="barra" aria-hidden="true">
            <div class="lleno" style="width: {elegida.pagada ? 100 : Math.round((e.pagado / e.total) * 100)}%"></div>
          </div>
        </div>

        {#if !elegida.pagada && liquidacion && liquidacion.cuotasQueEvita > 1}
          <!--
            Ley 1555 de 2012: prepagar es un derecho, sin penalidad, y lo que
            se paga se abona a capital. Por eso liquidar cuesta menos que la
            suma de las cuotas que faltan.
          -->
          <div class="aviso bueno">
            <p><strong>Si pagas todo hoy: {formatear(liquidacion.capital)}</strong></p>
            {#if liquidacion.ahorro > 0}
              <p class="explica">
                En vez de {formatear(liquidacion.siPagaCuotas)} que suman las
                {liquidacion.cuotasQueEvita} cuotas que faltan.
                Te ahorras <strong>{formatear(liquidacion.ahorro)}</strong> en intereses
                que no se causan. Prepagar es un derecho tuyo por ley y no
                puede tener penalidad.
              </p>
            {:else}
              <p class="explica">
                Es lo mismo que pagar las cuotas, porque esta compra no tiene
                intereses. Pagar antes no te ahorra plata, pero te la quita de
                encima.
              </p>
            {/if}
            <button class="secundario" onclick={() => abrirPago(null, true)}>
              Pagar todo
            </button>
          </div>
        {/if}

        <section>
          <h2>El calendario</h2>
          <ol class="plan">
            {#each filas as c (c.numero)}
              {@const vencida = !c.pagada && c.fecha < hoy()}
              <li class:pagada={c.pagada} class:vencida>
                <span class="num">{c.pagada ? '✓' : c.numero}</span>
                <div class="datos">
                  <p class="persona cifra">{formatear(c.valor)}</p>
                  <p class="meta">
                    {fechaLarga(c.fecha)}
                    {#if c.interes > 0} · {formatear(c.interes)} de interés{/if}
                    {#if vencida} · <span class="tarde">se pasó</span>{/if}
                    {#if !c.pagada && c.abonado > 0} · abonaste {formatear(c.abonado)}{/if}
                  </p>
                </div>
                {#if !c.pagada && !elegida.pagada}
                  <button class="pagar" onclick={() => abrirPago(c)}>Pagar</button>
                {/if}
              </li>
            {/each}
          </ol>
        </section>

        {#if !elegida.pagada}
          <section class="bloque">
            <h2>¿Quién paga cada cuota?</h2>
            <div class="chips" role="radiogroup" aria-label="Quién paga cada cuota">
              <button class="chip" class:activo={elegida.cobro !== 'debito_automatico'}
                      role="radio" aria-checked={elegida.cobro !== 'debito_automatico'}
                      onclick={() => cambiarCobro('manual')}>Yo la pago</button>
              <button class="chip" class:activo={elegida.cobro === 'debito_automatico'}
                      role="radio" aria-checked={elegida.cobro === 'debito_automatico'}
                      onclick={() => cambiarCobro('debito_automatico')}>Me la descuentan solos</button>
            </div>
            {#if elegida.cobro === 'debito_automatico'}
              <label>
                <span class="etiqueta">De qué cuenta</span>
                <select value={elegida.cuenta_debito_id}
                        onchange={(ev) => cambiarCobro('debito_automatico', ev.currentTarget.value)}>
                  {#each cuentasLiquidas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
                </select>
              </label>
              <p class="ayuda">
                {cuentaPorId[elegida.cuenta_debito_id]?.nombre ?? 'Esa cuenta'} la descuenta sola
                el día de la cuota y la app lo anota. Si no hay plata completa,
                se lleva lo que haya y el resto apenas entre. Deja la plata ahí
                antes del {e.proxima ? fechaCorta(e.proxima.fecha) : 'día de la cuota'}:
                lo que falte cobra intereses de mora.
              </p>
            {/if}
          </section>
        {/if}

        {#if costoDeDiferir(plan) > 0}
          <p class="ayuda">
            Esta compra cuesta {formatear(costoDeDiferir(plan))} de intereses
            si la pagas cuota a cuota{#if elegida.tasa_ea}{` (${String(elegida.tasa_ea).replace('.', ',')}% efectivo anual)`}{/if}.
            Eso se anota como gasto cada vez que pagas, no de golpe. Abonar
            antes baja los intereses: es un derecho tuyo por ley (Ley 1555 de 2012).
          </p>
        {/if}

        <button class="peligro" onclick={borrarCompra} disabled={guardando}>
          Borrar esta compra
        </button>

      <!-- ══ PAGAR ══════════════════════════════════════════════════════ -->
      {:else if vista === 'pagar' && elegida}
        <div class="destacado">
          <p class="rotulo">
            {liquidarTodo ? 'Liquidar ' + elegida.descripcion
             : `Cuota ${cuotaElegida?.numero} de ${elegida.cuotas_totales}`}
          </p>
          <p class="cifra grande">
            {formatear(liquidarTodo ? liquidacion.capital : cuotaElegida?.pendiente ?? 0)}
          </p>
          {#if liquidarTodo && liquidacion.ahorro > 0}
            <p class="frase">Te ahorras {formatear(liquidacion.ahorro)} en intereses.</p>
          {:else if !liquidarTodo && cuotaElegida?.interes > 0}
            <p class="frase">
              {formatear(cuotaElegida.capital)} de capital
              y {formatear(cuotaElegida.interes)} de interés.
            </p>
          {/if}
        </div>

        {@render comoPagaste()}

        <p class="ayuda">
          Este pago <strong>no vuelve a contar como gasto</strong>: ya se contó
          el día que compraste. Aquí solo baja la deuda de
          {cuentaPorId[elegida.cuenta_id]?.nombre ?? 'tu crédito'}.
          {#if !liquidarTodo && cuotaElegida?.interes > 0}
            Lo único que se anota como gasto nuevo son los
            {formatear(cuotaElegida.interes)} de interés.
          {/if}
        </p>

        <div class="botones">
          <button class="secundario" onclick={() => (vista = 'detalle')}>Cancelar</button>
          <button class="principal" disabled={guardando} onclick={registrarPago}>
            {guardando ? 'Guardando…' : 'Anotar el pago'}
          </button>
        </div>

      <!-- ══ PAGAR LA FACTURA DE UNA TARJETA ════════════════════════════ -->
      {:else if vista === 'factura' && facturaElegida}
        {@const f = facturaElegida.factura}
        {@const debeTodo = facturaElegida.compras.reduce((t, c) => t + estadoPlan(c.plan, c.abonos).pendiente, 0)}
        <div class="destacado">
          <p class="rotulo">Factura que se paga el {fechaLarga(f.limite)}</p>
          <p class="cifra grande">{formatear(f.total)}</p>
          <p class="frase">
            {#if f.interes > 0}
              {formatear(f.capital)} de las compras + {formatear(f.interes)} de intereses
            {:else}
              Sin intereses
            {/if}
          </p>
          {#if f.interes > 0}
            <p class="ayuda">
              Los intereses son un cálculo de la app. El valor exacto lo da
              {facturaElegida.cuenta.nombre} el día del corte: si es otro, pon el de ellos.
            </p>
          {/if}
        </div>

        <label>
          <span class="etiqueta">Cuánto pagaste</span>
          <input class="monto-grande cifra" type="text" inputmode="numeric" bind:value={montoFacturaTexto} />
          <span class="ayuda">
            Si pagas más de la factura, lo que sobra se abona a las cuotas que
            siguen y baja los intereses. Si pagas menos, se tapan primero las
            compras más viejas; lo que falte queda atrasado.
          </span>
        </label>

        {#if diferenciaFactura !== 0 && valorDelPago > 0}
          <div class="bloque">
            <span class="etiqueta">
              Son {formatear(Math.abs(diferenciaFactura))} {diferenciaFactura > 0 ? 'más' : 'menos'}
              de lo que calculó la app. ¿Por qué?
            </span>
            <div class="chips" role="radiogroup" aria-label="Por qué es distinto">
              {#if puedeSerInteres}
                <button class="chip" class:activo={modoDiferencia === 'interes'}
                        role="radio" aria-checked={modoDiferencia === 'interes'}
                        onclick={() => (diferenciaEs = 'interes')}>
                  Es lo que dice {facturaElegida.cuenta.nombre}
                </button>
              {/if}
              <button class="chip" class:activo={modoDiferencia === 'abono'}
                      role="radio" aria-checked={modoDiferencia === 'abono'}
                      onclick={() => (diferenciaEs = 'abono')}>
                {diferenciaFactura > 0 ? 'Estoy abonando de más' : 'Pagué menos de la factura'}
              </button>
            </div>
            <p class="ayuda">
              {#if modoDiferencia === 'interes'}
                Los intereses quedan como los cobró {facturaElegida.cuenta.nombre}:
                {formatear(f.interes + diferenciaFactura)}. Las cuotas quedan pagadas igual.
              {:else if diferenciaFactura > 0}
                Lo que sobra se abona a las cuotas que siguen: baja lo que debes y los intereses.
              {:else}
                Se tapan primero las compras más viejas; lo que falte queda atrasado y cobra mora.
              {/if}
            </p>
          </div>
        {/if}

        {@render comoPagaste()}

        {#if repartoFactura}
          <section>
            <h2>Así se reparte</h2>
            {#each repartoFactura.partes as p (p.compra.id)}
              <div class="cuota-mes sin-dia">
                <div class="datos">
                  <p class="persona">{p.compra.descripcion}</p>
                  <p class="meta">
                    {[...new Set(p.cubre)].length === 1 ? 'Cuota' : 'Cuotas'} {[...new Set(p.cubre)].join(', ')}
                    de {p.compra.plan.length}
                    {#if p.interes > 0} · {formatear(p.interes)} de interés{/if}
                  </p>
                </div>
                <span class="cifra monto">{formatear(p.monto)}</span>
              </div>
            {/each}
            {#if repartoFactura.sobrante > 0}
              <p class="error" role="alert">
                Eso es más de lo que debes en toda la tarjeta ({formatear(debeTodo)}).
                Sobran {formatear(repartoFactura.sobrante)}.
              </p>
            {/if}
          </section>
        {/if}

        <p class="ayuda">
          Este pago <strong>no vuelve a contar como gasto</strong>: cada compra
          se contó el día que la hiciste. Aquí baja la deuda de
          {facturaElegida.cuenta.nombre}; lo único que es gasto nuevo son los intereses.
        </p>

        <div class="botones">
          <button class="secundario" onclick={atras}>Cancelar</button>
          <button class="principal"
                  disabled={guardando || !repartoFactura?.partes.length || repartoFactura.sobrante > 0}
                  onclick={registrarPagoFactura}>
            {guardando ? 'Guardando…' : 'Anotar el pago'}
          </button>
        </div>

        {#if pagosTarjeta.length}
          <section>
            <h2>Pagos que ya hiciste a esta tarjeta</h2>
            {#each pagosTarjeta as p (p.id)}
              <div class="cuota-mes">
                <span class="dia cifra">{fechaCorta(p.fecha)}</span>
                <div class="datos">
                  <p class="persona">{formatear(p.monto)}</p>
                  <p class="meta">desde {cuentaPorId[p.cuenta_origen]?.nombre ?? 'otra cuenta'}</p>
                </div>
                <button class="enlace" disabled={guardando} onclick={() => deshacerPagoTarjeta(p)}>Deshacer</button>
              </div>
            {/each}
          </section>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<!-- Cuándo, de qué cuenta, cómo y su 4x1000: igual para una cuota que para una factura. -->
{#snippet comoPagaste()}
  <div class="par">
    <label>
      <span class="etiqueta">Cuándo pagaste</span>
      <div class="fecha-hora">
        <input type="date" bind:value={fechaPago} max={hoy()} />
        <input type="time" bind:value={horaPago} aria-label="Hora (opcional)" />
      </div>
    </label>
    <label>
      <span class="etiqueta">De qué cuenta</span>
      <select bind:value={cuentaPago} onchange={() => { if (gmfManual !== 0) gmfManual = null; }}>
        {#each cuentasLiquidas as c}<option value={c.id}>{c.icono ?? ''} {c.nombre}</option>{/each}
      </select>
    </label>
  </div>

  {#if !pagoEsEfectivo}
    <div class="chips" role="radiogroup" aria-label="Cómo pagaste">
      {#each METODOS_PAGO as [valor, etiqueta]}
        <button
          class="chip" class:activo={metodoPago === valor}
          role="radio" aria-checked={metodoPago === valor}
          onclick={() => { metodoPago = valor; if (gmfManual !== 0) gmfManual = null; }}
        >{etiqueta}</button>
      {/each}
    </div>
  {/if}

  <div class="gmf">
    {#if gmfPago > 0}
      <span>4x1000: <strong class="cifra">{formatear(gmfPago)}</strong></span>
      <button class="enlace" onclick={() => (gmfManual = 0)}>No me lo cobraron</button>
    {:else}
      <span class="sin-gmf">
        Sin 4x1000
        {#if gmfManual === 0}(dijiste que no te lo cobraron){:else if gmfAutoPago.razon}({gmfAutoPago.razon}){/if}
      </span>
      {#if gmfManual === 0 && gmfAutoPago.monto > 0}
        <button class="enlace" onclick={() => (gmfManual = null)}>Sí me lo cobraron</button>
      {:else if gmfAutoPago.monto === 0 && !pagoEsEfectivo}
        <button class="enlace" onclick={() => (gmfManual = calcularGmf(valorDelPago))}>Sí me lo cobraron</button>
      {/if}
    {/if}
  </div>
{/snippet}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold) }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-4);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }
  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }
  .explica.ojo { color: var(--alerta) }
  .ayuda { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }
  .tarde { color: var(--alerta) }

  .vacio { padding: var(--e-8) 0; text-align: center }
  .vacio-titulo {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    margin-bottom: var(--e-2);
  }

  /* ── La caja de arriba ── */
  .destacado {
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .destacado.saldada { opacity: 0.8 }
  .rotulo { font-size: var(--t-sm); color: var(--ink-3) }
  .grande {
    font-size: var(--t-3xl);
    font-weight: var(--w-bold);
    letter-spacing: var(--track-tight);
    line-height: var(--lh-tight);
  }
  .frase { font-size: var(--t-sm); color: var(--ink-2); margin-top: var(--e-1) }
  .frase.tarde { color: var(--alerta) }

  .barra {
    height: 6px;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    margin-top: var(--e-3);
    overflow: hidden;
  }
  .lleno {
    height: 100%;
    background: var(--acento);
    border-radius: var(--r-full);
    transition: width var(--d-normal) var(--ease-salida);
  }

  /* ── Cuotas del mes ── */
  .cuota-mes {
    display: grid;
    grid-template-columns: 52px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .cuota-mes.vencida { border-color: var(--alerta) }
  .cuota-mes.sin-dia { grid-template-columns: 1fr auto }

  /* ── Factura de tarjeta: un solo pago para todas sus compras ── */
  .factura {
    display: flex;
    flex-direction: column;
    gap: var(--e-1);
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-left: 3px solid var(--acento, var(--border));
    border-radius: var(--r-lg);
  }
  .factura.vencida { border-color: var(--alerta) }
  .factura-cabeza {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--e-3);
  }
  .factura .ayuda { margin-bottom: var(--e-2) }
  .bloque { display: flex; flex-direction: column; gap: var(--e-2) }
  .dia {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--ink-3);
    text-align: center;
    text-transform: uppercase;
  }

  /* ── Filas ── */
  .fila {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    text-align: left;
    width: 100%;
    transition: transform var(--d-instant) var(--ease-salida);
  }
  .fila:active { transform: scale(0.99) }
  .fila.cerrada { opacity: 0.62 }
  .datos { min-width: 0 }
  .derecha { text-align: right }
  .persona { font-weight: var(--w-medium) }
  .meta { font-size: var(--t-xs); color: var(--ink-3) }
  .meta.tarde { color: var(--alerta) }
  .monto { font-weight: var(--w-semibold); white-space: nowrap }
  .monto.apagado { color: var(--ink-3); font-weight: var(--w-medium) }

  .plegable {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: var(--e-2) 0;
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }
  .flecha { transition: transform var(--d-rapido) var(--ease-salida); font-size: var(--t-lg) }
  .flecha.abierta { transform: rotate(90deg) }

  /* ── El calendario de cuotas ── */
  .plan { list-style: none; display: flex; flex-direction: column; gap: var(--e-2) }
  .plan li {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-md);
  }
  .plan li.pagada { opacity: 0.55 }
  .plan li.vencida { border-color: var(--alerta) }
  .num {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    font-variant-numeric: tabular-nums;
  }
  .plan li.pagada .num { background: var(--acento); color: var(--on-acento) }
  .pagar {
    padding: var(--e-1) var(--e-3);
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    min-height: 34px;
  }
  .previo .plan li { background: var(--bg-sunken); border-style: dashed }

  /* ── Formularios ── */
  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input, select {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }
  .monto-grande { font-size: var(--t-2xl); font-weight: var(--w-bold); text-align: center }

  .chips { display: flex; gap: var(--e-2); flex-wrap: wrap }
  .chip {
    min-width: 48px;
    min-height: var(--tap);
    padding: 0 var(--e-3);
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    font-variant-numeric: tabular-nums;
  }
  .chip.activo { background: var(--acento); color: var(--on-acento) }
  .chip-num { width: 72px; min-height: var(--tap); text-align: center }

  .aviso {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    border-left: 3px solid var(--acento);
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    font-size: var(--t-sm);
  }
  .aviso.bueno { border-left-color: var(--ingreso) }
  .casilla {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--e-2);
    font-size: var(--t-sm);
    color: var(--ink-2);
    line-height: var(--lh-loose);
  }
  .casilla input { width: 20px; height: 20px; min-height: 0; margin-top: 2px; accent-color: var(--acento); flex-shrink: 0 }
  .corto { max-width: 96px }
  .error {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--alerta-suave);
    color: var(--alerta);
    font-size: var(--t-sm);
    line-height: var(--lh-loose);
  }

  /* ── El 4x1000 del pago ── */
  .gmf {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--e-2);
    flex-wrap: wrap;
    font-size: var(--t-sm);
    padding: var(--e-2) var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }
  .sin-gmf { color: var(--ink-3) }
  .enlace {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    color: var(--acento);
    text-decoration: underline;
    text-underline-offset: 2px;
    min-height: 32px;
  }
  /* Dentro de la caja, el botón secundario tendría el mismo fondo que ella y
     no se leería como botón. Se le da la superficie y un borde. */
  .aviso .secundario {
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
  }

  .opciones { display: flex; flex-direction: column; gap: var(--e-2) }
  .opcion {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    padding: var(--e-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    background: var(--bg-surface);
    transition: border-color var(--d-instant) var(--ease-salida);
  }
  .opcion.activa { border-color: var(--acento); box-shadow: inset 0 0 0 1px var(--acento) }
  .opcion-titulo { font-size: var(--t-sm); font-weight: var(--w-semibold) }
  .opcion-nota { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-loose) }

  .botones { display: flex; gap: var(--e-2) }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal.ancho { width: 100% }
  .principal:disabled { opacity: 0.4 }
  .secundario {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .peligro {
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: transparent;
    color: var(--peligro);
    font-weight: var(--w-medium);
    min-height: var(--tap);
    border: 1px solid var(--border-strong);
  }
  /* Fecha y hora juntas: el orden dentro del día también cuenta. */
  .fecha-hora { display: flex; flex-direction: column; gap: var(--e-1) }
</style>
